import React from "react";
import { Plus, Trash2, Video, Link2, ListChecks, FileText, Code2, MessageSquare } from "lucide-react";

const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-indigo-400/50";
const labelCls = "text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1";

export function LessonBlockEditor({ data, onChange }) {
  return (
    <div className="space-y-3" data-testid="block-editor-lesson">
      <div>
        <label className={labelCls}>Lesson content</label>
        <textarea value={data.content || ""} onChange={(e) => onChange({ ...data, content: e.target.value })} rows={6}
          placeholder="Write today's lesson (separate paragraphs with a blank line)..." data-testid="lesson-block-content-input" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}><Video className="w-3 h-3 inline mr-1" />Embedded video URL (optional)</label>
        <input value={data.video_url || ""} onChange={(e) => onChange({ ...data, video_url: e.target.value })}
          placeholder="https://youtube.com/watch?v=..." data-testid="lesson-block-video-input" className={inputCls} />
      </div>
    </div>
  );
}

export function ResourcesBlockEditor({ data, onChange }) {
  const items = data.items || [];
  const update = (i, field, val) => onChange({ items: items.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)) });
  const add = () => onChange({ items: [...items, { label: "", url: "" }] });
  const remove = (i) => onChange({ items: items.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-2" data-testid="block-editor-resources">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={it.label} onChange={(e) => update(i, "label", e.target.value)} placeholder="Label" data-testid={`resource-label-input-${i}`} className={`${inputCls} w-1/3`} />
          <input value={it.url} onChange={(e) => update(i, "url", e.target.value)} placeholder="https://..." data-testid={`resource-url-input-${i}`} className={inputCls} />
          <button onClick={() => remove(i)} data-testid={`resource-remove-btn-${i}`} className="text-slate-500 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={add} data-testid="resource-add-btn" className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200 mt-1">
        <Plus className="w-3.5 h-3.5" /> Add resource link
      </button>
    </div>
  );
}

export function ProjectBlockEditor({ data, onChange }) {
  return (
    <div className="space-y-3" data-testid="block-editor-mini_project">
      <div>
        <label className={labelCls}><FileText className="w-3 h-3 inline mr-1" />Mini-project brief</label>
        <textarea value={data.description || ""} onChange={(e) => onChange({ ...data, description: e.target.value })} rows={4}
          placeholder="Describe the hands-on task for today..." data-testid="project-block-description-input" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Submission type</label>
        <select value={data.submission_type || "text"} onChange={(e) => onChange({ ...data, submission_type: e.target.value })}
          data-testid="project-block-submission-type-select" className={inputCls}>
          <option value="text">Text answer</option>
          <option value="file">File upload</option>
          <option value="link">Link (repo/deployed URL)</option>
        </select>
      </div>
    </div>
  );
}

export function QuizBlockEditor({ data, onChange }) {
  const questions = data.questions || [];
  const updateQ = (qi, field, val) => onChange({ questions: questions.map((q, i) => (i === qi ? { ...q, [field]: val } : q)) });
  const updateOpt = (qi, oi, val) => onChange({
    questions: questions.map((q, i) => (i === qi ? { ...q, options: q.options.map((o, j) => (j === oi ? val : o)) } : q)),
  });
  const addQ = () => onChange({ questions: [...questions, { question: "", options: ["", "", "", ""], correct_index: 0 }] });
  const removeQ = (qi) => onChange({ questions: questions.filter((_, i) => i !== qi) });

  return (
    <div className="space-y-4" data-testid="block-editor-quiz">
      {questions.map((q, qi) => (
        <div key={qi} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`quiz-question-editor-${qi}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400"><ListChecks className="w-3 h-3 inline mr-1" />Question {qi + 1}</span>
            <button onClick={() => removeQ(qi)} data-testid={`quiz-remove-question-btn-${qi}`} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <input value={q.question} onChange={(e) => updateQ(qi, "question", e.target.value)} placeholder="Question text"
            data-testid={`quiz-question-input-${qi}`} className={`${inputCls} mb-2`} />
          <div className="space-y-1.5">
            {q.options.map((opt, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input type="radio" checked={q.correct_index === oi} onChange={() => updateQ(qi, "correct_index", oi)}
                  data-testid={`quiz-correct-radio-${qi}-${oi}`} className="accent-indigo-500" />
                <input value={opt} onChange={(e) => updateOpt(qi, oi, e.target.value)} placeholder={`Option ${oi + 1}`}
                  data-testid={`quiz-option-input-${qi}-${oi}`} className={inputCls} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={addQ} data-testid="quiz-add-question-btn" className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200">
        <Plus className="w-3.5 h-3.5" /> Add question
      </button>
    </div>
  );
}

export function CodePlaygroundBlockEditor({ data, onChange }) {
  return (
    <div className="space-y-3" data-testid="block-editor-code_playground">
      <div>
        <label className={labelCls}><Code2 className="w-3 h-3 inline mr-1" />Language</label>
        <select value={data.language || "python"} onChange={(e) => onChange({ ...data, language: e.target.value })}
          data-testid="code-block-language-select" className={inputCls}>
          <option value="python">Python</option>
          <option value="javascript">JavaScript</option>
        </select>
      </div>
      <div>
        <label className={labelCls}>Task description</label>
        <textarea value={data.description || ""} onChange={(e) => onChange({ ...data, description: e.target.value })} rows={2}
          placeholder="What should the learner build/fix?" data-testid="code-block-description-input" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Starter code</label>
        <textarea value={data.starter_code || ""} onChange={(e) => onChange({ ...data, starter_code: e.target.value })} rows={5}
          placeholder="def solve():\n    pass" data-testid="code-block-starter-input" className={`${inputCls} font-mono`} />
      </div>
    </div>
  );
}

export function DiscussionBlockEditor({ data, onChange }) {
  return (
    <div className="space-y-3" data-testid="block-editor-discussion">
      <div>
        <label className={labelCls}><MessageSquare className="w-3 h-3 inline mr-1" />Reflection prompt</label>
        <textarea value={data.prompt || ""} onChange={(e) => onChange({ ...data, prompt: e.target.value })} rows={3}
          placeholder="e.g. What was the hardest part of today's lesson, and how did you work through it?" data-testid="discussion-block-prompt-input" className={inputCls} />
      </div>
      <p className="text-[11px] text-slate-500">Learners will write their own answer here and see peer answers below it.</p>
    </div>
  );
}

export function BlockEditorFor({ type, data, onChange }) {
  if (type === "lesson") return <LessonBlockEditor data={data} onChange={onChange} />;
  if (type === "resources") return <ResourcesBlockEditor data={data} onChange={onChange} />;
  if (type === "mini_project") return <ProjectBlockEditor data={data} onChange={onChange} />;
  if (type === "quiz") return <QuizBlockEditor data={data} onChange={onChange} />;
  if (type === "code_playground") return <CodePlaygroundBlockEditor data={data} onChange={onChange} />;
  if (type === "discussion") return <DiscussionBlockEditor data={data} onChange={onChange} />;
  return null;
}

export const BLOCK_DEFAULTS = {
  lesson: { content: "", video_url: "" },
  resources: { items: [] },
  mini_project: { description: "", submission_type: "text" },
  quiz: { questions: [] },
  code_playground: { language: "python", starter_code: "", description: "" },
  discussion: { prompt: "" },
};

export const BLOCK_META = {
  lesson: { label: "Lesson", icon: FileText, color: "#5C9DFF" },
  resources: { label: "Resources", icon: Link2, color: "#34D399" },
  mini_project: { label: "Mini-Project", icon: FileText, color: "#FBBF24" },
  quiz: { label: "Quiz", icon: ListChecks, color: "#F472B6" },
  code_playground: { label: "Code Playground", icon: Code2, color: "#22D3EE" },
  discussion: { label: "Discussion", icon: MessageSquare, color: "#A78BFA" },
};
